import Button from "@app/components/Button/Button";
import ArrowRightIcon from "@assets/icons/arrow-right.svg?react";
import CategoryTag from "@components/CategoryTag";
import Link from "@components/Link";
import VideoPlayButton from "@src/app/assets/icons/video-playbutton.svg?react";
import PlaceholderImage from "@src/app/assets/images/placeholder.svg";
import type { BFFArticleCard } from "@src/app/types/bff";
import clsx from "clsx";
import dayjs from "dayjs";

import DescriptionTag from "../DescriptionTag";

type FeatureCardProps = {
  article: BFFArticleCard;
  imageAlt?: string;
  className?: string;
};

export default function FeatureCard({
  article,
  imageAlt = "",
  className,
}: FeatureCardProps) {
  const {
    cover,
    sectionName = "",
    section,
    title,
    description,
    authors,
    urlPath,
    created,
    displayType,
  } = article;

  const descriptionTag =
    displayType === "big_read"
      ? "BigRead"
      : displayType === "photo_story"
        ? "PhotoStory"
        : undefined;

  const isVideo = displayType == "video";

  return (
    <article
      data-testid={`feature-card-${article.id}`}
      className={clsx(
        "group flex w-full flex-col gap-5 px-3 py-4 lg:flex-row lg:items-stretch",
        className,
      )}
    >
      <Link
        to={urlPath}
        className="relative aspect-3/2 w-full overflow-hidden lg:flex-1"
      >
        <img
          alt={imageAlt}
          className="h-full w-full object-cover transition-transform duration-300 lg:group-hover:scale-105"
          src={cover || PlaceholderImage}
        />
        {descriptionTag ? (
          <div className="absolute top-0 right-0">
            <DescriptionTag name={descriptionTag} />
          </div>
        ) : null}

        {isVideo ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <VideoPlayButton />
          </div>
        ) : null}
      </Link>

      <div className="lg:bg-background-secondary flex flex-col lg:-ml-5 lg:flex-1 lg:justify-between lg:px-5 lg:py-4">
        <div className="flex flex-col gap-3">
          <CategoryTag name={sectionName} href={`/${section}`} />

          <Link to={urlPath} className="flex flex-col gap-3">
            {/* Title */}
            <h3 className="text-header-2 text-primary hover:text-brand-primary lg:line-clamp-5">
              {title}
            </h3>

            {/* Description */}
            <p className="text-body-2 text-secondary line-clamp-6">
              {description}
            </p>

            {/* Author(s) */}
            {authors.length > 0 && (
              <p
                className={
                  "text-label-2 text-tertiary dark:text-secondary-on-dark break-word line-clamp-1 w-full font-bold wrap-anywhere"
                }
              >
                {authors.slice(0, 2).map((author, index) => (
                  <span key={author.id}>
                    {author.name}
                    {index < Math.min(authors.length, 2) - 1 && ", "}
                  </span>
                ))}
              </p>
            )}

            {/* Created Date */}
            <p className="text-label-2 text-tertiary dark:text-secondary-on-dark break-word line-clamp-1 w-full font-bold wrap-anywhere">
              {dayjs(created).format("DD MMM YYYY")}
            </p>
          </Link>
        </div>
        <div className="hidden lg:flex lg:justify-end">
          <Link to={urlPath}>
            <Button
              variant="primary"
              size="medium"
              icon={<ArrowRightIcon className="size-6" />}
              iconPosition="right"
              className="h-8"
            >
              Read more
            </Button>
          </Link>
        </div>
      </div>
    </article>
  );
}
